import * as ReactDOM from "react-dom";
import * as React from "react";
import { ButtonUse } from "@skbkontur/react-ui";

import { CandyThemeWrapper } from "../../../../../Forms/CandyThemeWrapper";
import { isTestingMode } from "../../../../../Controls/Testing";
import { nextTick } from "../../../../../../../Common/AsyncUtils";
import { MergeResultModal } from "../../../../Core/ModalManager/Modals/Utils/MergeResult/MergeResult";
import { CombineFufResult } from "../../../../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import { SessionExpiredModal } from "../../../../../Controls/Services/SessionExpiredModal";
import { ThemeVariant } from "../../../../../Controls/Internals/ThemeProvider/ThemeProvider";

import { ILongOperationProgress, IUIUtil } from "./IUIUtil";
import { LongOperationModal } from "./LongOperationModal/LongOperationModal";
import {
    ConfirmMiniModal,
    ConfirmMiniModalProps,
    MiniModalButtonsDirection,
} from "./ConfirmMiniModal/ConfirmMiniModal";

interface LongOperationOptions {
    title: string;
    modalSpecialCase?: "NdflEmployeeImport" | "InfiniteProcess";
    allowCancel?: boolean;
}

interface MiniModalConfirm {
    message?: null | string | React.ReactNode;
    confirmButtonText?: null | string;
    discardButtonText?: string;
    headerText?: string;
    iconName?: string;
    onlyConfirm?: boolean;
    width?: string;
    buttonsDirection?: MiniModalButtonsDirection;
}

export class UIUtil implements IUIUtil {
    private modalNode?: HTMLDivElement;
    private readonly theme: ThemeVariant | undefined;

    public constructor(theme: ThemeVariant | undefined) {
        this.theme = theme;
    }

    public confirm(
        message?: null | string | React.ReactNode,
        confirmButtonText?: null | string,
        discardButtonText?: string,
        headerText?: string,
        onlyConfirm?: boolean,
        iconName?: string,
        submitButtonUse?: ButtonUse,
        width?: string,
        buttonsDirection?: MiniModalButtonsDirection
    ): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            const onDiscardHandler = () => {
                resolve(false);
                ReactDOM.unmountComponentAtNode(domNode);
            };
            //Для тестов, чтобы была возможность доджить окно
            if (isTestingMode()) {
                window.__CANDY_FORMS_TESTING_MODE__SkipUIUtilsModal = onDiscardHandler;
            }
            const domNode = this.getNodeForModalElements();
            const commonProps: ConfirmMiniModalProps = {
                confirmButtonText: confirmButtonText ?? "OK",
                discardButtonText: discardButtonText,
                headerText: headerText,
                text: message ?? "Выполнить действие?",
                onDiscard: !onlyConfirm ? onDiscardHandler : undefined,
                onConfirm: () => {
                    resolve(true);
                    ReactDOM.unmountComponentAtNode(domNode);
                },
                submitButtonUse: submitButtonUse,
                width: width,
                buttonsDirection: buttonsDirection,
            };

            ReactDOM.render(
                <CandyThemeWrapper theme={this.theme}>
                    <ConfirmMiniModal {...commonProps} iconName={iconName} />
                </CandyThemeWrapper>,
                domNode
            );
        });
    }

    public miniModalConfirm(props: MiniModalConfirm) {
        return this.confirm(
            props.message,
            props.confirmButtonText,
            props.discardButtonText,
            props.headerText,
            true,
            props.iconName,
            undefined,
            props.width,
            props.buttonsDirection
        );
    }

    public mergeResult(props: { mergeResult?: CombineFufResult; documentName?: string; headerText?: string }) {
        return new Promise<void>(resolve => {
            const domNode = this.getNodeForModalElements();
            ReactDOM.render(
                <CandyThemeWrapper theme={this.theme}>
                    <MergeResultModal
                        {...props}
                        onClose={() => {
                            ReactDOM.unmountComponentAtNode(domNode);
                            resolve();
                        }}
                    />
                </CandyThemeWrapper>,
                domNode
            );
        });
    }

    public sessionExpired() {
        return new Promise<void>(() => {
            const domNode = this.getNodeForModalElements();
            ReactDOM.render(
                <CandyThemeWrapper theme={this.theme}>
                    <SessionExpiredModal />
                </CandyThemeWrapper>,
                domNode
            );
        });
    }

    public async executeLongOperation(
        titleOrOptions: string | LongOperationOptions,
        executeCallback: (progress: ILongOperationProgress, signal: AbortSignal) => Promise<void>
    ): Promise<void> {
        const domNode = this.getNodeForModalElements();
        const options = typeof titleOrOptions === "string" ? { title: titleOrOptions } : titleOrOptions;
        await nextTick();
        return new Promise<void>((resolve, reject) => {
            ReactDOM.render(
                <CandyThemeWrapper theme={this.theme}>
                    <LongOperationModal
                        specialCase={options.modalSpecialCase}
                        title={options.title}
                        allowCancel={options.allowCancel ?? false}
                        executeCallback={executeCallback}
                        onClose={() => {
                            ReactDOM.unmountComponentAtNode(domNode);
                            resolve();
                        }}
                        onError={e => {
                            ReactDOM.unmountComponentAtNode(domNode);
                            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                            reject(e);
                        }}
                    />
                </CandyThemeWrapper>,
                domNode
            );
        });
    }

    private getNodeForModalElements(): HTMLElement {
        if (this.modalNode == undefined) {
            this.modalNode = window.document.createElement("div");
            window.document.body.appendChild(this.modalNode);
        }
        return this.modalNode;
    }
}
